import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  apiLogin, apiSignup, apiLogout, apiGetMe, apiUpdateProfile, apiDismissTour, hasStoredTokens,
  apiGoogleLogin, apiAppleLogin,
} from '../utils/backendApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true); // only true on first load
  const [loading, setLoading] = useState(false); // true during login/signup attempts

  // Restore session on mount
  useEffect(() => {
    async function restore() {
      if (hasStoredTokens()) {
        try {
          const me = await apiGetMe();
          if (me) setUser(me);
        } catch { /* token expired */ }
      }
      setInitializing(false);
    }
    restore();
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const userData = await apiLogin(email, password);
      setUser(userData);
      setLoading(false);
      return userData;
    } catch (err) {
      setLoading(false);
      // Re-throw so LoginPage can display the error
      throw err;
    }
  }, []);

  // Single entry point used by the LoginPage's Google / Apple buttons.
  // Both providers return our standard user object on success; thrown
  // errors carry `code` so the LoginPage can detect PENDING_APPROVAL and
  // route the user to the right messaging.
  const loginWithGoogle = useCallback(async (idToken) => {
    setLoading(true);
    try {
      const userData = await apiGoogleLogin(idToken);
      setUser(userData);
      return userData;
    } finally { setLoading(false); }
  }, []);

  const loginWithApple = useCallback(async (idToken, applePayload = null) => {
    setLoading(true);
    try {
      const userData = await apiAppleLogin(idToken, applePayload);
      setUser(userData);
      return userData;
    } finally { setLoading(false); }
  }, []);

  const signup = useCallback(async (name, email, password, extras = {}) => {
    setLoading(true);
    try {
      const result = await apiSignup(name, email, password, extras);
      setLoading(false);
      return result;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    const updated = await apiUpdateProfile(profileData);
    setUser(updated);
    return updated;
  }, []);

  const dismissTour = useCallback(async () => {
    try {
      const updated = await apiDismissTour();
      setUser(updated);
    } catch (e) {
      // Non-fatal — mark locally so the tour doesn't replay even if the API failed
      setUser(prev => prev ? { ...prev, has_seen_tour: true } : prev);
      console.error('[AuthContext] dismissTour failed:', e.message);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initializing, login, signup, logout, updateProfile, dismissTour, loginWithGoogle, loginWithApple }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
